// Assets live in a private S3 bucket fronted by a CloudFront distribution (OAC),
// so they are served through plain, permanent URLs instead of presigned S3 links.
// Objects keep their S3 key, so mapping is a pure host swap: drop the signature
// query string and point the path at the distribution. No API call, no expiry
// check, no re-signing - neither at render time nor on click.
//
// This covers every asset type (images, PDFs, video). The distribution is not a
// catch-all: an unknown key answers 403, so a rewritten URL resolves only if the
// object is really there.
const CDN_HOST = process.env.NEXT_PUBLIC_ASSET_CDN_HOST || "dka1n2ezdhmui.cloudfront.net";

// AWS S3-style host (e.g. *.s3.eu-central-1.amazonaws.com or *.s3.amazonaws.com).
// Matches both the current bucket and the legacy one - CloudFront serves the
// same keys for both, so a single rule covers them.
const isAmazonS3Host = (hostname: string): boolean =>
    /(^|\.)s3([.-][a-z0-9-]+)?\.amazonaws\.com$/.test(hostname);

/**
 * Rewrites an S3 asset URL to its CloudFront equivalent. External URLs (YouTube,
 * Vimeo, placeholders), relative paths and anything unparsable are returned
 * unchanged. The extension is irrelevant: the key is what CloudFront serves.
 */
export const toCdnAssetUrl = (url: string): string => {
    try {
        const parsed = new URL(url);
        if (!isAmazonS3Host(parsed.hostname)) return url;

        return `https://${CDN_HOST}${parsed.pathname}`;
    } catch {
        return url;
    }
};

// Asset attributes inside a Strapi HTML string: `src` (images, video) and `href`
// (PDF links). Both are a plain host swap now that documents are on the CDN too.
const ASSET_ATTR = /(src|href)(\s*=\s*)(["'])([^"']+)\3/gi;

/**
 * Takes an HTML string (e.g. Strapi rich text) and rewrites every S3 asset URL
 * in a `src` or `href` attribute to its CloudFront equivalent.
 */
export const rewriteAssetUrlsInHtml = (html: string): string => {
    if (!html) return html;

    return html.replace(
        ASSET_ATTR,
        (match, attr, eq, quote, url) => `${attr}${eq}${quote}${toCdnAssetUrl(url)}${quote}`
    );
};

// Cheap gate: the vast majority of strings in a Strapi payload hold no URL at
// all, so one substring scan keeps them away from the regex and the URL parser.
const S3_MARKER = "amazonaws.com";
const HAS_ASSET_ATTR = /(src|href)\s*=\s*["']/i;

const rewriteAssetString = (value: string): string => {
    if (!value.includes(S3_MARKER)) return value;

    return HAS_ASSET_ATTR.test(value) ? rewriteAssetUrlsInHtml(value) : toCdnAssetUrl(value);
};

/**
 * Rewrites every S3 asset URL inside a parsed API payload - both plain URL
 * fields (`url`, `formats.*.url`) and URLs embedded in HTML strings.
 *
 * This runs at the fetch boundary so no presigned URL ever enters the app.
 * It matters beyond the rendered markup: React serializes the raw Strapi
 * objects into the RSC payload, signature query strings and all, even for the
 * format variants nothing ends up displaying.
 *
 * Mutates in place - the input is a freshly parsed response that nothing else
 * holds a reference to, and cloning a multi-megabyte payload to change a few
 * strings is not worth it.
 */
export const rewriteAssetUrlsDeep = <T>(payload: T): T => {
    const walk = (node: any): void => {
        if (Array.isArray(node)) {
            for (let i = 0; i < node.length; i++) {
                const value = node[i];
                if (typeof value === "string") node[i] = rewriteAssetString(value);
                else if (value && typeof value === "object") walk(value);
            }
            return;
        }

        for (const key of Object.keys(node)) {
            const value = node[key];
            if (typeof value === "string") node[key] = rewriteAssetString(value);
            else if (value && typeof value === "object") walk(value);
        }
    };

    if (payload && typeof payload === "object") walk(payload);

    return payload;
};
